import { createOrganization } from '@/api/organizations'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { queryKeys } from '@/lib/query-keys'
import { useOrganizationStore } from '@/store/organization'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ArrowRight, Code, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

/**
 * Branding sidebar component
 */
const BrandingSidebar = () => {
  return (
    <div className="hidden flex-col justify-center items-center p-12 lg:flex lg:w-1/2 bg-primary/10">
      <div className="max-w-xl">
        <Code size={64} className="mb-8 text-primary" />
        <h1 className="mb-4 text-4xl font-bold">Sully.ai API Dashboard</h1>
        <p className="mb-6 text-xl text-muted-foreground">
          Create your organization to manage API keys, webhooks, billing and
          usage for your health AI integration.
        </p>

        <div className="mt-8 space-y-4">
          <h3 className="text-xl font-semibold">Your console provides:</h3>
          <ul className="space-y-3">
            <li className="flex items-center">
              <div className="p-1 mr-3 rounded-full bg-primary/20">
                <ArrowRight size={16} className="text-primary" />
              </div>
              <span>Secure API key management</span>
            </li>
            <li className="flex items-center">
              <div className="p-1 mr-3 rounded-full bg-primary/20">
                <ArrowRight size={16} className="text-primary" />
              </div>
              <span>Webhook configuration</span>
            </li>
            <li className="flex items-center">
              <div className="p-1 mr-3 rounded-full bg-primary/20">
                <ArrowRight size={16} className="text-primary" />
              </div>
              <span>Usage analytics & billing</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

/**
 * Schema for organization creation form
 */
const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: 'Organization name must be at least 2 characters.' })
    .max(100, {
      message: 'Organization name must be less than 100 characters.',
    }),
})

type FormValues = z.infer<typeof formSchema>

/**
 * Organization creation form view
 */
export const CreateOrganizationView = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const setOrganization = useOrganizationStore((s) => s.setOrganization)
  const isSwitching = useOrganizationStore((s) => s.isSwitching)
  const queryClient = useQueryClient()

  const createOrganizationMutation = useMutation({
    mutationFn: async (name: string) => {
      return createOrganization(name)
    },
    onSuccess: (organization) => {
      void setOrganization(organization)

      void queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all(),
      })
    },
    onError: (err) => {
      console.error('Failed to create organization:', err)
      setErrorMessage('Failed to create organization. Please try again.')
    },
  })

  const isLoading = createOrganizationMutation.isPending || isSwitching

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '' },
  })

  const onSubmit = (values: FormValues) => {
    setErrorMessage(null)
    createOrganizationMutation.mutate(values.name)
  }

  return (
    <div className="flex w-full h-screen bg-background">
      <BrandingSidebar />

      {/* Form Panel */}
      <div className="flex flex-col justify-center items-center p-6 w-full lg:w-1/2 md:p-12">
        <div className="space-y-8 w-full max-w-md">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">
              Create Your Organization
            </h2>
            <p className="mt-3 text-muted-foreground">
              Set up your organization to get started with our platform
            </p>
          </div>

          {errorMessage && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base">
                      Organization Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Acme Inc."
                        {...field}
                        autoFocus
                        disabled={isLoading}
                        className="h-12 text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full h-12 text-base group"
                disabled={isLoading}
                size="lg"
              >
                {isLoading ? (
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                ) : (
                  'Create Organization'
                )}
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
